import { HString } from '@shared/src/datatypes/HString';
import { HTags } from '@shared/src/datatypes/HTags';
import { ShipmentDeliveryLineBatchInfoDto } from '@shared/src/dtos/shipment/delivery/ShipmentDeliveryLineBatchInfoDto';
import { PalletTypes } from '@shared/src/enums/PalletTypes';
import { IDeliveryLine } from './IDeliveryLine';

/***
 * Quines columnes de la taula de línies s'han de pintar.
 *
 * Regla (acordada amb el client): en EDICIÓ es mostren totes, perquè
 * l'usuari pot voler passar un camp de 0 a un valor. En VISUALITZACIÓ una
 * columna només es mostra si alguna línia hi té valor; si totes valen 0 o
 * estan buides entenem que aquest enviament no treballa amb aquesta dada i
 * no l'ensenyem.
 *
 * Aquesta classe és l'únic punt on es decideix la visibilitat, de manera que
 * si algun dia s'hi vol endollar la configuració per client (la pantalla
 * "Configuració de propietats") només cal tocar aquí.
 *
 * ⚠️ IMPORTANT si afegeixes un camp nou (com es va fer amb boxes/bags/sacks):
 * afegir-lo aquí (propietat + línia a compute()) NO ÉS SUFICIENT. Cada
 * component que edita/mostra línies li ha de dir explícitament a `compute()`
 * (paràmetre `supported`, veure `isSupported` més avall) que sap pintar
 * aquest camp; si no hi és a la llista `supported` d'AQUEST component
 * concret, la columna quedarà SEMPRE amagada (edició i visualització, amb
 * dades o sense) i no saltarà cap error.
 *
 * Llocs on cal afegir el nom del camp (grep "supportedColumns\|let supported\|SUPPORTEDCOLUMNS"
 * dins `front/projects/shared/src/components/route` per trobar-los tots, per si n'hi ha algun de nou):
 *  - shared.shipment.delivery.component.ts          → supportedColumns()  (albarà "teòric" del shipment)
 *  - updatedeliverylinesdialog.dialog.component.ts  → supportedColumns()  (línies executades del CMR)
 *  - shared.route.part.cmr.digitaldocument.editable.component.ts       → variable local `supported`
 *  - shared.route.part.shipmentdeliverynote.digitaldocument.component.ts → SUPPORTEDCOLUMNS
 *
 * I si el camp ve de ShipmentDeliveryLine i també ha de sortir al CMR/eCMR,
 * a més d'IDeliveryLine cal afegir-lo a ECMRDeliveryLineDto.ts (i al DTO/Object
 * Java corresponent — veure ECMRDeliveryLineObject/DTO i CalculateECMRDeliveryLinesScope).
 *
 * CAS PARTICULAR — publicTags / privateTags: privateTags NOMÉS es coneix al
 * costat "teòric" del shipment (ShipmentDeliveryLineDto); les línies del
 * CMR (ECMRDeliveryLineDto) només tenen publicTags. Per això privateTags és
 * `supported` a shared.shipment.delivery.component.ts i a
 * shared.route.part.shipmentdeliverynote.digitaldocument.component.ts, però
 * NO a updatedeliverylinesdialog.dialog.component.ts ni a
 * shared.route.part.cmr.digitaldocument.editable.component.ts.
 */
export class DeliveryLineColumns {

    public reference: boolean = true;
    public description: boolean = true;
    public quantity: boolean = true;
    public units: boolean = true;
    public kg: boolean = true;
    public netKg: boolean = true;
    public boxes: boolean = true;
    public bags: boolean = true;
    public sacks: boolean = true;
    public litres: boolean = true;
    public wDimension: boolean = true;
    public yDimension: boolean = true;
    public hDimension: boolean = true;
    public m3: boolean = true;
    public palletCount: boolean = true;
    public palletType: boolean = true;
    public unitPrice: boolean = true;
    public totalPrice: boolean = true;
    public packageId: boolean = true;
    public userTags: boolean = true;
    public publicTags: boolean = true;
    public privateTags: boolean = true;
    public batchInfo: boolean = true;

    /***
     * @param lines les línies que es pintaran
     * @param editMode true = formulari d'edició (es mostra tot)
     * @param supported camps que aquest component sap pintar; els que no
     *        estiguin a la llista queden amagats SEMPRE (p.e. el CMR no té
     *        preus ni paquets). Aquesta llista la passa cada component
     *        concret (veure el comentari a dalt de la classe): si un camp
     *        nou no hi és, aquesta funció no en dirà res — simplement no
     *        el mostrarà mai. Si un camp "no surt" i les línies sí que hi
     *        tenen valor, el primer que cal mirar és aquest paràmetre.
     */
    public static compute(lines: IDeliveryLine[], editMode: boolean, supported: string[]): DeliveryLineColumns {
        let result = new DeliveryLineColumns();

        // supported == null vol dir "ho suporta tot" (cap component el passa null avui,
        // però compute([]) en test sí); si no és null, només es mostra el que hi és llistat.
        let isSupported = (name: string) => supported == null || supported.indexOf(name) >= 0;

        // reference i description sempre es mostren (assumim que sempre hi ha descripció)
        result.reference = isSupported('reference');
        result.description = isSupported('description');

        let safeLines = lines != null ? lines : [];

        let anyNumber = (pick: (l: IDeliveryLine) => number): boolean =>
            safeLines.some(l => {
                let value = pick(l);
                return value != null && +value !== 0;
            });

        let anyText = (pick: (l: IDeliveryLine) => string): boolean =>
            safeLines.some(l => !HString.isNullOrNullString(pick(l)));

        let show = (name: string, hasValue: () => boolean): boolean => {
            if (!isSupported(name))
                return false;
            if (editMode)
                return true;
            return hasValue();
        };

        result.quantity = show('quantity', () => anyNumber(l => l.quantity));
        result.units = show('units', () => anyText(l => l.units));
        result.kg = show('kg', () => anyNumber(l => l.kg));
        result.netKg = show('netKg', () => anyNumber(l => l.netKg));
        result.boxes = show('boxes', () => anyNumber(l => l.boxes));
        result.bags = show('bags', () => anyNumber(l => l.bags));
        result.sacks = show('sacks', () => anyNumber(l => l.sacks));
        result.litres = show('litres', () => anyNumber(l => l.litres));
        result.wDimension = show('wDimension', () => anyNumber(l => l.wDimension));
        result.yDimension = show('yDimension', () => anyNumber(l => l.yDimension));
        result.hDimension = show('hDimension', () => anyNumber(l => l.hDimension));
        result.m3 = show('m3', () => anyNumber(l => l.m3));
        result.unitPrice = show('unitPrice', () => anyNumber(l => l.unitPrice));
        result.totalPrice = show('totalPrice', () => anyNumber(l => l.totalPrice));
        result.packageId = show('packageId', () => anyNumber(l => l.packageId));
        result.userTags = show('userTags', () => safeLines.some(l => HTags.getTags(l.userTags).length > 0));
        result.publicTags = show('publicTags', () => safeLines.some(l => HTags.getTags(l.publicTags).length > 0));
        result.privateTags = show('privateTags', () => safeLines.some(l => HTags.getTags(l.privateTags).length > 0));

        // palletCount i palletType van juntes: si no hi ha cap palet, no té sentit ensenyar el tipus
        let anyPallet = () => anyNumber(l => l.palletCount)
            || safeLines.some(l => l.palletType != null && l.palletType !== PalletTypes.None);
        result.palletCount = show('palletCount', anyPallet);
        result.palletType = show('palletType', anyPallet);

        // Els lots segueixen la mateixa regla que la resta: si cap línia no en té, en
        // visualització no s'ensenya el bloc. Si no, sortiria sempre una taula de lots
        // buida i, pitjor, faria aparèixer el chevron a línies que no tenen res a desplegar.
        result.batchInfo = show('batchInfo',
            () => safeLines.some(l => ShipmentDeliveryLineBatchInfoDto.countBatches(l.batchInfo) > 0));

        return result;
    }
}
